/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import React, { ReactElement } from 'react';
import { StoreApi, UseBoundStore } from 'zustand';
import { SdsDivProps } from '../Component.props';
import { DialogContent } from './Dialog.defs';
import { DialogStore } from './Dialog.state';
import { WizardStep } from './Wizard/Wizard.defs';
import { DIALOG_SIZE_MD, DialogSize } from './types/DialogSize.types';
import { DIALOG_TYPE_STANDARD, DialogType } from './types/DialogType.types';

/**
 * The props for `<Dialog />`.
 */
export interface DialogProps extends SdsDivProps {
  /**
   * Button key identifiers.
   */
  buttonKeys?: Array<string>;

  /**
   * The dialog buttons.
   */
  buttons?: Array<JSX.Element>;

  /**
   * Text for the close button.
   */
  closeButtonText?: string;

  /**
   * The dialog content.
   */
  dialogContent?: DialogContent;

  /**
   * The dialog size.
   */
  dialogSize?: DialogSize;

  /**
   * The dialog type.
   */
  dialogType?: DialogType;

  /**
   * The error message to display.
   */
  errorMessage?: string | null;

  /**
   * The footer text.
   */
  footer?: string | ReactElement;

  /**
   * Prevent fullscreen dialog in mobile view.
   */
  preventFullScreen?: boolean;

  /**
   * Button to toggle between modal size & full screen size.
   */
  showFullScreenButton?: boolean;

  /**
   * The dialog heading.
   */
  heading: string;

  /**
   * An icon to accompany the heading.
   */
  icon?: JSX.Element;

  /**
   * The dialog displays a loading state.
   */
  loading?: boolean;

  /**
   * The dialog displays a loading state message.
   */
  loadingMessage?: React.ReactNode;

  /**
   * Close handler.
   */
  onClose?(): void;

  /**
   * The dialog content should be scrollable on overflow.
   */
  scrollOnOverflow?: boolean;

  /**
   * The dialog content gets dividing lines.
   */
  showDividers?: boolean;

  /**
   * The state store to handle the appearance of the dialog.
   */
  stateHandler: UseBoundStore<StoreApi<DialogStore>>;

  /**
   * The dialog subtitle.
   */
  subtitle?: string;

  /**
   * The steps available within the wizard dialog.
   */
  wizardSteps?: Array<WizardStep>;
}

/**
 * The props for `<Dialog />` content.
 */
export interface DialogContentProps extends SdsDivProps {
  /**
   * The function to close the dialog.
   */
  close(): void;

  /**
   * The additional props passed to the dialog content.
   */
  customProps?: object;

  /**
   * The function to call after closing the dialog.
   */
  setAfterClose?: React.Dispatch<React.SetStateAction<(() => void) | undefined>>;

  /**
   * The function to add button keys unique to the dialog content.
   */
  setButtonKeys: React.Dispatch<React.SetStateAction<Array<string>>>;

  /**
   * The function to add buttons unique to the dialog content.
   */
  setButtons: React.Dispatch<React.SetStateAction<Array<JSX.Element>>>;
}

/**
 * The default prop values for `<Dialog />`.
 */
export const dialogDefaults = {
  buttonKeys: [],
  buttons: [],
  closeButtonText: `Close`,
  dialogContent: { renderContent: (): JSX.Element => <></> },
  dialogSize: DIALOG_SIZE_MD,
  dialogType: DIALOG_TYPE_STANDARD,
  scrollOnOverflow: true,
  wizardSteps: [],
};
